import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { FileText, Plus } from "lucide-react";
import type { LivingDocItem } from "./LivingDocBlock";
import { LivingDocBlock } from "./LivingDocBlock";

interface Props {
  items: LivingDocItem[];
  onUpdateNote: (id: string, text: string) => void | Promise<void>;
  onRemove: (id: string) => void | Promise<void>;
  onReorder: (ids: string[]) => void | Promise<void>;
  onInsertAt?: (index: number) => void;
  /** Render a custom empty state when items is empty. */
  emptyState?: React.ReactNode;
}

/**
 * B2.1 + B2.6 — Living-doc block list with keyboard-accessible drag&drop.
 *
 * Uses @dnd-kit/sortable. Persists order by emitting `onReorder(ids)` with the
 * full ordered list; consumer is responsible for `PATCH /items/order`.
 *
 * Between every pair of items renders a "+ Añadir aquí" divider that fires
 * `onInsertAt(index)`. At the tail we render the same affordance.
 */
export function LivingDocBlocks({
  items,
  onUpdateNote,
  onRemove,
  onReorder,
  onInsertAt,
  emptyState,
}: Props) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIdx = items.findIndex((i) => i.id === active.id);
    const toIdx = items.findIndex((i) => i.id === over.id);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = items.slice();
    const [moved] = next.splice(fromIdx, 1);
    if (moved) next.splice(toIdx, 0, moved);
    void onReorder(next.map((i) => i.id));
  }

  if (items.length === 0) {
    return (
      <>{emptyState ?? <DefaultEmpty onAdd={onInsertAt ? () => onInsertAt(0) : undefined} />}</>
    );
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <ol className="space-y-3" data-testid="living-doc-blocks">
          {items.map((item, idx) => (
            <li key={item.id} className="group">
              <SortableBlock item={item} onUpdateNote={onUpdateNote} onRemove={onRemove} />
              {onInsertAt && idx < items.length - 1 && (
                <Divider onClick={() => onInsertAt(idx + 1)} />
              )}
            </li>
          ))}
        </ol>
        {onInsertAt && <Divider onClick={() => onInsertAt(items.length)} tail />}
      </SortableContext>
    </DndContext>
  );
}

function SortableBlock({
  item,
  onUpdateNote,
  onRemove,
}: {
  item: LivingDocItem;
  onUpdateNote: (id: string, text: string) => void | Promise<void>;
  onRemove: (id: string) => void | Promise<void>;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };
  // dnd-kit's listeners must be applied to the actual drag handle button.
  // We pass them down via dragHandleProps so the rest of the block doesn't
  // initiate drag on hover-click.
  return (
    <div ref={setNodeRef} style={style}>
      <LivingDocBlock
        item={item}
        onUpdateNote={onUpdateNote}
        onRemove={onRemove}
        isDragging={isDragging}
        dragHandleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}

function Divider({ onClick, tail }: { onClick: () => void; tail?: boolean }) {
  return (
    <div
      className={`flex items-center justify-center ${tail ? "mt-3" : "my-2"}`}
      data-testid="living-doc-divider"
    >
      <button
        type="button"
        onClick={onClick}
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-(--border-default) bg-transparent px-3 py-0.5 text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary) hover:text-(--accent-600) hover:border-(--accent-400) transition"
      >
        <Plus size={10} /> Añadir aquí
      </button>
    </div>
  );
}

function DefaultEmpty({ onAdd }: { onAdd?: () => void }) {
  return (
    <div
      data-testid="living-doc-empty"
      className="rounded-lg border border-dashed border-(--border-strong) bg-(--bg-elevated) p-6 text-center"
    >
      <FileText size={20} className="mx-auto mb-2 text-(--text-tertiary)" />
      <h3 className="text-sm font-semibold mb-1">Vacío de momento</h3>
      <p className="text-xs text-(--text-tertiary) mb-3">
        Selecciona texto en un documento y elige <strong>"→ Apuntes"</strong>, o crea una nota
        propia abajo.
      </p>
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          data-testid="living-doc-empty-add"
          className="inline-flex items-center gap-1 rounded-md border border-(--accent-500) bg-(--accent-500) px-3 py-1.5 text-xs font-medium text-white hover:bg-(--accent-600)"
        >
          <Plus size={11} /> Nueva nota
        </button>
      )}
    </div>
  );
}
